import { Link, usePage } from '@inertiajs/react'
import { formatLempiras } from '../../lib/format'
import { useCart, CartProvider, Cart } from '../../lib/cart'
import CartHeader from '../../lib/CartHeader'

interface Account { id: number; name: string; slug: string; logo_url?: string | null }
interface Branch { id: number; name: string; address?: string; phone?: string }
interface Props { account: Account; branches: Branch[]; cart?: Cart }

export default function CatalogCart(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogCartInner {...props} />
    </CartProvider>
  )
}

function CatalogCartInner({ account }: { account: Account; branches: Branch[] }) {
  const { items, updateQuantity, removeItem } = useCart()
  const flash = (usePage().props as { flash?: { notice?: string; alert?: string } }).flash
  const subtotal = items.reduce((sum, i) => sum + (i.unit_price_cents * i.quantity), 0)
  const discount = items.reduce((sum, i) => sum + (i.discount_cents ?? 0), 0)
  const tax = items.reduce((s, i) => {
    const rate = i.tax_rate ?? 15
    const lineSubtotal = i.unit_price_cents * i.quantity
    const lineDiscounted = lineSubtotal - (i.discount_cents ?? 0)
    return s + (lineSubtotal > 0 ? Math.round(lineDiscounted * (rate / 100)) : 0)
  }, 0)
  const total = subtotal - discount + tax

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />

      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline text-sm mb-4 inline-block">
          ← Volver al catálogo
        </Link>
        <h1 className="text-2xl font-bold text-[#202020] mb-6">Tu Carrito</h1>

        {flash?.alert && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            {flash.alert}
          </div>
        )}
        {flash?.notice && (
          <div className="mb-4 rounded-lg bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">
            {flash.notice}
          </div>
        )}

        {items.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-3">Tu carrito está vacío</p>
            <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline">
              Ver catálogo
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {items.map((item, idx) => {
                const tracks = item.track_inventory === true
                const max = tracks ? Number(item.stock_quantity ?? 0) : null
                const canIncrease = max === null || item.quantity < max
                return (
                <div key={idx} className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-[#202020]">{item.product_name}</p>
                    {item.variant_name && <p className="text-sm text-gray-500">{item.variant_name}</p>}
                    <p className="text-[#55ACEE] font-bold">{formatLempiras(item.unit_price_cents)}</p>
                    {(item.promotions ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(item.promotions ?? []).map((p, i) => (
                          <span key={i} className="inline-block bg-green-100 text-green-700 text-[10px] font-medium px-1.5 py-0.5 rounded">
                            🏷️ {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {(item.discount_cents ?? 0) > 0 && (
                      <p className="text-[11px] text-green-600 font-medium mt-0.5">
                        Ahorro: −{formatLempiras(item.discount_cents!)}
                      </p>
                    )}
                    {tracks && (
                      <p className="text-xs text-gray-500">Stock: {max ?? 0}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(idx, item.quantity - 1)}
                      className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300">−</button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    {canIncrease && (
                      <button onClick={() => updateQuantity(idx, item.quantity + 1)}
                        className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300">+</button>
                    )}
                  </div>
                  <p className="font-bold text-[#202020] w-24 text-right">{formatLempiras(item.unit_price_cents * item.quantity - (item.discount_cents ?? 0))}</p>
                  <button onClick={() => removeItem(idx)}
                    className="text-red-500 hover:text-red-700 ml-2">✕</button>
                </div>
                )
              })}
            </div>

            <div className="bg-white rounded-lg shadow p-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>{formatLempiras(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600 font-medium">
                  <span>Descuento promociones</span>
                  <span>−{formatLempiras(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-600">
                <span>ISV</span>
                <span>{formatLempiras(tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-[#202020] pt-2 border-t">
                <span>Total</span>
                <span>{formatLempiras(total)}</span>
              </div>
            </div>

            <Link
              href={`/${account.slug}/catalog/checkout`}
              className="block w-full text-center bg-[#FFC107] text-[#202020] py-3 rounded font-semibold hover:bg-[#e6ad06] mt-4"
            >
              Continuar
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
